import { useMemo } from 'react';
import { css } from '@emotion/react';
import { Form, Packet } from '@weave/schema-gen-ts/dist/schemas/forms-digital/weave_digital_forms.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { checkDocumentIsAccessible } from '@frontend/auto-message-hooks';
import { useFormsFreemiumFeatureFlag } from '@frontend/digital-forms-scope';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { LocationChips } from '@frontend/location-chips';
import { RadioSelectionChip } from '@frontend/radio-selection-chip';
import { FORMS_ACTIVE_STATES, FeatureUpgradeModalWrapper } from '@frontend/self-upgrade';
import { theme } from '@frontend/theme';
import {
  Button,
  Heading,
  Modal,
  ModalControlModalProps,
  ModalControlResponse,
  NakedButton,
  RadioField,
  SearchField,
  SwitchField,
  Text,
  useForm,
  useModalControl,
  useTooltip,
  variantsTheme,
  XIconSmall,
} from '@frontend/design-system';

type DocumentWithType = (Form | Packet) & { documentType?: string };

interface Props {
  documentId?: string;
  documents: DocumentWithType[];
  modalProps: ModalControlModalProps;
  selectedLocationIds: string[];
  updateDocumentId: (id: string) => void;
}

interface FormItemProps {
  document: DocumentWithType;
  isAccessible: boolean;
  isLocked: boolean;
  upgradeModalControls: ModalControlResponse;
}

const FormItem = ({ document, isAccessible, isLocked, upgradeModalControls }: FormItemProps) => {
  const { t } = useTranslation('messages');
  const {
    Tooltip: IsLockedTooltip,
    tooltipProps: isLockedTooltipProps,
    triggerProps: isLockedTriggerProps,
  } = useTooltip({
    placement: 'bottom',
  });
  const {
    Tooltip: NotAccessibleTooltip,
    tooltipProps: notAccessibleTooltipProps,
    triggerProps: notAccessibleTriggerProps,
  } = useTooltip({
    placement: 'bottom',
  });
  const isDisabled = !isAccessible || isLocked;
  const showIsLockedTooltip = !upgradeModalControls.modalProps.show; // Prevent tooltip from showing when upgrade modal is open

  return (
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        borderBottom: `1px solid ${theme.colors.neutral10}`,
        '& > div': {
          padding: theme.spacing(2),
          marginBottom: 0,
          flex: 1,
        },
      }}
      // TODO: do we need to spread both tooltips props if both apply?
      {...(isLocked && showIsLockedTooltip ? isLockedTriggerProps : {})}
      {...(!isAccessible ? notAccessibleTriggerProps : {})}
    >
      <RadioField.Radio
        value={document.id ?? ''}
        disabled={isDisabled}
        css={[
          {
            '& > div': {
              display: 'none',
            },
            label: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              marginLeft: 0,
            },
          },
          isDisabled && {
            '.single-location-chip, .multi-location-chip': variantsTheme.disabled,
          },
        ]}
      >
        <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          <Icon name='forms' />
          <Text color={isDisabled ? 'disabled' : 'default'} weight={isDisabled ? 'regular' : 'bold'}>
            {document.name}
          </Text>
        </div>
        <LocationChips filteredLocationIds={document.businessGroupIds ?? []} />
      </RadioField.Radio>

      <NotAccessibleTooltip {...notAccessibleTooltipProps}>
        {document.documentType === 'packet'
          ? t('Not all locations have access to this digital packet.')
          : t('Not all locations have access to this digital form.')}
      </NotAccessibleTooltip>

      <IsLockedTooltip
        {...isLockedTooltipProps}
        css={{
          backgroundColor: theme.colors.white,
          color: theme.colors.neutral50,
          '& .tooltip-floating-arrow': {
            fill: theme.colors.white,
          },
        }}
      >
        {t('To use this form, upgrade your Forms Add-on.')}
        <br />
        <Button
          variant='tertiary'
          css={{
            padding: 0,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
          onClick={upgradeModalControls.openModal}
        >
          {t('Upgrade Now')}
        </Button>
      </IsLockedTooltip>
    </div>
  );
};

export const DocumentSelectorModal = ({
  documentId,
  documents,
  modalProps,
  selectedLocationIds,
  updateDocumentId,
}: Props) => {
  const { t } = useTranslation('messages');
  const { isFreemium } = useFormsFreemiumFeatureFlag();
  const upgradeModalControls = useModalControl();

  const { getFieldProps } = useForm({
    fields: {
      document: { type: 'radio', value: documentId ?? '' },
      documentViewType: { type: 'radio', value: 'form' },
      search: { type: 'text', value: '' },
      showAccessibleOnly: { type: 'switch', value: false },
      sortDirection: { type: 'radio', value: 'asc' },
    },
  });

  const documentFieldProps = getFieldProps('document');
  const documentViewTypeFieldProps = getFieldProps('documentViewType');
  const searchFieldProps = getFieldProps('search');
  const showAccessibleOnlyFieldProps = getFieldProps('showAccessibleOnly');
  const sortDirectionFieldProps = getFieldProps('sortDirection');

  const displayedDocuments = useMemo(() => {
    let documentsToDisplay = documents;

    // freemium mode - only show forms
    if (isFreemium) {
      documentsToDisplay = documentsToDisplay.filter((doc) => doc.documentType === 'form');
    }

    // filter by selected document view type chip
    // freemium mode would be the same as the 'form' document view type, but we are being verbose for clarity
    if (documentViewTypeFieldProps.value === 'form') {
      documentsToDisplay = documentsToDisplay.filter((doc) => doc.documentType === 'form');
    } else {
      documentsToDisplay = documentsToDisplay.filter((doc) => doc.documentType === 'packet');
    }

    // filter by accessibility to selected locations
    if (showAccessibleOnlyFieldProps.value) {
      documentsToDisplay = documentsToDisplay.filter((document) =>
        checkDocumentIsAccessible(selectedLocationIds, document.businessGroupIds ?? [])
      );
    }

    // filter by search
    documentsToDisplay = documentsToDisplay.filter((document) =>
      document.name?.toLowerCase()?.includes(searchFieldProps.value.toLowerCase())
    );

    // sort by name
    documentsToDisplay = documentsToDisplay.sort((a, b) => {
      const nameA = a.name?.toLowerCase() ?? '';
      const nameB = b.name?.toLowerCase() ?? '';

      if (nameA < nameB) return sortDirectionFieldProps.value === 'asc' ? -1 : 1;
      if (nameA > nameB) return sortDirectionFieldProps.value === 'asc' ? 1 : -1;
      return 0;
    });

    return documentsToDisplay;
  }, [
    documents,
    documentViewTypeFieldProps.value,
    isFreemium,
    searchFieldProps.value,
    selectedLocationIds,
    showAccessibleOnlyFieldProps.value,
    sortDirectionFieldProps.value,
  ]);

  const options = useMemo(() => {
    return [
      { value: 'form', label: t('Forms') },
      isFreemium ? undefined : { value: 'packet', label: t('Packets') },
    ].filter((document): document is NonNullable<{ value: 'form' | 'packet'; label: string }> => !!document);
  }, [isFreemium, t]);

  const noDisplayedDocuments = displayedDocuments.length === 0;

  return (
    <>
      <Modal
        {...modalProps}
        css={{
          [`@media (min-width: 768px)`]: { minWidth: theme.spacing(71) },
        }}
      >
        <Modal.Header>
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <div css={{ display: 'flex', gap: theme.spacing(1) }}>
              <Heading level={2}>{t('Select Digital Form')}</Heading>
              <LocationChips filteredLocationIds={selectedLocationIds} />
            </div>
            <NakedButton onClick={modalProps.onClose}>
              <XIconSmall />
            </NakedButton>
          </div>
        </Modal.Header>
        <Modal.Body
          css={{
            overflowY: 'hidden',
            gap: 0,
          }}
        >
          <div
            css={{
              display: 'flex',
              gap: theme.spacing(1),
              alignItems: 'center',
            }}
          >
            {options.length > 1 && (
              <RadioSelectionChip
                fieldProps={{
                  ...documentViewTypeFieldProps,
                  onChange: (e) => {
                    // @ts-ignore type in DS is not correct
                    const selectedValue = e.target.value;
                    documentViewTypeFieldProps.onChange({ ...documentViewTypeFieldProps, value: selectedValue });
                  },
                }}
                options={options}
                css={{
                  height: theme.spacing(5),
                  button: {
                    '&.selected-chip': {
                      border: `1px solid ${theme.colors.primary50}`,
                    },
                    '&.unselected-chip': {
                      border: `1px solid ${theme.colors.neutral30}`,
                    },
                    height: '100%',
                    label: {
                      display: 'flex',
                      alignItems: 'center',
                      height: '100%',
                      border: '1px solid transparent',
                    },
                    '&:focus-within': {
                      outline: 'none',
                      outlineOffset: 0,
                    },
                  },
                }}
              />
            )}
            <SearchField
              {...searchFieldProps}
              id='search'
              name='search'
              placeholder={t('Search')}
              containerCss={css({ width: '100%' })}
            />
            <Button
              css={{
                border: `1px solid ${theme.colors.neutral30}`,
                height: theme.spacing(5),
              }}
              iconName={sortDirectionFieldProps.value === 'desc' ? 'sort-descending' : 'sort-ascending'}
              onClick={() => {
                const newDirection = sortDirectionFieldProps.value === 'desc' ? 'asc' : 'desc';
                sortDirectionFieldProps.onChange({ ...sortDirectionFieldProps, value: newDirection });
              }}
              variant='secondary'
            />
          </div>
          <SwitchField
            {...showAccessibleOnlyFieldProps}
            label={t('Accessible to all locations')}
            css={{
              padding: theme.spacing(1.5, 0),
              borderBottom: `1px solid ${theme.colors.neutral10}`,
            }}
          />
          <div
            css={{
              width: '100%',
              overflowY: 'auto',
              height: 400,
              ...(noDisplayedDocuments && {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }),
            }}
          >
            {noDisplayedDocuments ? (
              <Text>{t('There are no forms to attach.')}</Text>
            ) : (
              <RadioField
                {...{
                  ...documentFieldProps,
                  onChange: (e) => {
                    documentFieldProps.onChange(e);
                    // @ts-ignore type in DS is not correct
                    const selectedValue = e.target.value;
                    updateDocumentId(selectedValue);
                  },
                }}
                label=''
                name='document'
                css={{
                  overflowY: 'auto',
                  '& > label': {
                    display: 'none',
                  },
                  '& > div': {
                    marginBottom: 0,
                  },
                }}
              >
                {displayedDocuments.map((document) => {
                  const form = document as Form;

                  // users can only attach forms and packets to a template if the form/packet is accessible to all associated locations on the rule
                  const isAccessible = checkDocumentIsAccessible(selectedLocationIds, document.businessGroupIds ?? []);

                  const isLocked = isFreemium ? !!form.isLocked : false;

                  return (
                    <FormItem
                      key={document.id}
                      document={document}
                      isAccessible={isAccessible}
                      isLocked={isLocked}
                      upgradeModalControls={upgradeModalControls}
                    />
                  );
                })}
              </RadioField>
            )}
          </div>
        </Modal.Body>
      </Modal>
      <FeatureUpgradeModalWrapper
        modalControls={upgradeModalControls}
        feature={Feature.FORMS}
        activeStates={FORMS_ACTIVE_STATES}
      />
    </>
  );
};
